<script setup lang="ts">
import '@/assets/scss/form.scss'
import { permisions } from '@/onenetwork/constants/onenetwork.perm.const.ts'
import { Icon } from '@iconify/vue'
import { useConfirm } from 'primevue'
import { useToast } from 'primevue/usetoast'
import { computed, ref } from 'vue'
import { useAccountBODetailStore } from '../stores/account.bo.detail.store.ts'

const accountBODetailStore = useAccountBODetailStore()

const confirm = useConfirm()
const toast = useToast()

interface Props {
  id: number | null
  editable: boolean
}

const emit = defineEmits<{
  (e: 'update:perms', perms: string[]): void
}>()
const props = defineProps<Props>()



const perms = computed(() => {
  if (!props?.id || !accountBODetailStore.accounts[props?.id]) {
    return []
  }
  return accountBODetailStore.accounts[props?.id]?.account?.permissions
})
const editable = computed(() => {
  return props.editable
})

const permCreate = ref<string[]>([])

const getCurrentPermissions = (): string[] => {
  return props.id ? perms.value : permCreate.value
}

const applyPermissions = (updatedPerms: string[]) => {
  if (props.id) {
    accountBODetailStore.setAccount(props.id, {
      permissions: updatedPerms,
    })
    return
  }

  permCreate.value = updatedPerms
  emit('update:perms', permCreate.value)
}

const getSectionPermissionKeys = (sectionKey: string): string[] => {
  const children = permisions[sectionKey]?.child || {}
  return Object.keys(children).map((childKey) => `${sectionKey}.${childKey}`)
}

const isSectionChecked = (sectionKey: string): boolean => {
  const sectionPerms = getSectionPermissionKeys(sectionKey)
  if (sectionPerms.length === 0) {
    return false
  }

  const currentPerms = getCurrentPermissions()
  return sectionPerms.every((perm) => currentPerms.includes(perm))
}

const isSectionIndeterminate = (sectionKey: string): boolean => {
  const sectionPerms = getSectionPermissionKeys(sectionKey)
  if (sectionPerms.length === 0) {
    return false
  }

  const currentPerms = getCurrentPermissions()
  const selectedCount = sectionPerms.filter((perm) => currentPerms.includes(perm)).length

  return selectedCount > 0 && selectedCount < sectionPerms.length
}

const handleToggleSection = (sectionKey: string) => {
  const currentPerms = getCurrentPermissions()
  const sectionPerms = getSectionPermissionKeys(sectionKey)

  if (isSectionChecked(sectionKey)) {
    applyPermissions(currentPerms.filter((perm) => !sectionPerms.includes(perm)))
    return
  }

  const mergedPerms = [...new Set([...currentPerms, ...sectionPerms])]
  applyPermissions(mergedPerms)
}

const togglePermission = (list: string[], key: string): string[] => {
  if (list?.length > 0) {
    return list.includes(key) ? list.filter((item) => item !== key) : [...list, key]
  } else {
    return [key]
  }
}

const handleCheckPerm = (_e: Event, key: string) => {
  const updatedPerms = togglePermission(getCurrentPermissions(), key)
  applyPermissions(updatedPerms)
}
</script>

<template>
  <template v-for="(key, fidx) in Object.keys(permisions)" :key="`perm-heading-${fidx}`">
    <div class="flex items-center gap-4 justify-between">
      <h4 class="flex items-center font-bold">
        <Icon icon="carbon:subtract-large" width="32" height="32" /> {{ permisions[key].name }}
      </h4>
      <label v-if="Object.keys(permisions[key].child).length > 0" class="flex items-center gap-2 text-sm hover:text-[#FF0000] cursor-pointer">
        <input
          type="checkbox"
          :checked="isSectionChecked(key)"
          :disabled="!editable"
          :indeterminate.prop="isSectionIndeterminate(key)"
          @click="() => handleToggleSection(key)"
        />
        <span>Chọn tất cả</span>
      </label>
    </div>
    <template v-if="Object.keys(permisions[key].child).length > 0">
      <div class="grid grid-cols-3 pl-10 py-5" v-if="editable">
        <label
          class="flex p-1 gap-2 hover:text-[#FF0000] cursor-pointer"
          v-for="(k, idx) in Object.keys(permisions[key].child)"
          :key="`perm-item-${idx}`"
        >
          <input
            type="checkbox"
            :checked="getCurrentPermissions().includes(`${key}.${k}`)"
            @click="(e: MouseEvent) => handleCheckPerm(e, `${key}.${k}`)"
          />
          <span>{{ permisions[key].child[k].name }}</span>
        </label>
      </div>
      <div class="grid grid-cols-3 pl-10 py-5" v-else>
        <div
          class="flex p-1 gap-2"
          v-for="(k, idx) in Object.keys(permisions[key].child)"
          :key="`perm-item-${idx}`"
        >
          <input type="checkbox" :checked="getCurrentPermissions().includes(`${key}.${k}`)" disabled />
          <span>{{ permisions[key].child[k].name }}</span>
        </div>
      </div>
    </template>
  </template>
</template>

<style scoped></style>
